/**
 * Wraps a legacy paid page (the onboarding planning slides, guided planning, the old report and
 * checkout pages) with the self-funding route guard. A consumer-flow client who opens one, usually
 * from an old bookmark, is sent into the consumer flow instead, before the page mounts, so guided
 * planning never creates a session for them. Advisors, wizards and other clients see the page as
 * before. The router builds these wrappers once, so page identity stays stable.
 */
import type { ComponentType } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useSelfFundRouteGuard } from '../hooks/use-self-fund-route-guard';

export function withSelfFundRouteGuard<TProps extends object>(
  Page: ComponentType<TProps>,
  consumerDestination: 'entry' | 'self-fund' = 'entry',
): ComponentType<TProps> {
  function GuardedLegacyPage(props: TProps) {
    const { clientId = '' } = useParams();
    const decision = useSelfFundRouteGuard(clientId, {
      kind: 'legacy-paid-page',
      consumerDestination,
    });

    if (decision.kind === 'redirect') {
      return (
        <Navigate
          to={decision.to}
          replace
        />
      );
    }
    return <Page {...props} />;
  }
  GuardedLegacyPage.displayName = `withSelfFundRouteGuard(${Page.displayName ?? Page.name ?? 'Page'})`;
  return GuardedLegacyPage;
}
